import AppInputSearch from '@nxsys/components/AppInputSearch';
import AppMenuFilter from '@nxsys/components/AppMenuFilter';
import AppLoader from '@nxsys/components/AppLoader';
import AppsContent from '@nxsys/components/AppsContainer/AppsContent';
import AppsHeader from '@nxsys/components/AppsContainer/AppsHeader';
import AppTableContainer from '@nxsys/components/AppTableContainer';
import TaxYearSelect from '@nxsys/components/TaxYearSelect';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppState } from '@nxsys/store';
import { aepnActions } from '@nxsys/store/slices/aepn/reducer/aepnSlice';
import { IPayroll } from '@nxsys/types/models/payroll';
import { getTaxWeekTaxYear, showCurrencyByCountry } from '@nxsys/utils';
import { PayrollCountryType } from '@nxsys/types/models/Common';
import { defaultPaymentFrequency } from '@nxsys/constants/AppConstants';
import { showMessage } from '@nxsys/utils/message';
import { formatMoneyExport } from '@nxsys/utils/number-helper';
import { ExportToCsv } from 'export-to-csv';
import { debounce, find } from 'lodash';
import moment from 'moment';
import { useCallback, useEffect, useImperativeHandle, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ContributionsFilterModal, {
  ContributionsFilterValues,
} from '../components/ContributionsFilterModal';
import { getContributionsPendingColumns } from './contributions-columns';
import { ContributionsIrelandTabRef } from './ContributionsReportIreland';

type Props = {
  tabRef: React.RefObject<ContributionsIrelandTabRef>;
};

const ContributionsPendingTab: React.FC<Props> = ({ tabRef }) => {
  const { messages } = useTypedIntl();
  const dispatch = useDispatch();
  const { payrollCountry, isBureauEmployer, user, isBureau } = useSelector(
    (state: AppState) => state.appStore,
  );
  const { payrollsPending, actionLoading } = useSelector(
    (state: AppState) => state.aepnStore,
  );
  const { list, totalDocs, loading } = payrollsPending;

  const currentTaxYear = getTaxWeekTaxYear().taxYear;
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [queryParams, setQueryParams] = useState<Record<string, any>>({
    taxYear: currentTaxYear,
  });
  const [selectedRows, setSelectedRows] = useState<IPayroll[]>([]);
  const [isOpenFilterModal, setIsOpenFilterModal] = useState(false);

  const fetchData = useCallback(
    (params: Record<string, any>) => {
      const merged = { ...queryParams, ...params };
      setQueryParams(merged);
      if (params.page !== undefined) setPage(params.page);
      const reqParams: Record<string, any> = { ...merged };
      if (isBureauEmployer && user?.companyUserIds?.length) {
        reqParams.companyIds = user.companyUserIds;
      }
      dispatch(aepnActions.getPayrollPending(reqParams) as any);
    },
    [queryParams, isBureauEmployer, user],
  );

  useEffect(() => {
    fetchData({ page: 0, size: 20, taxYear: currentTaxYear });
  }, []);

  useImperativeHandle(tabRef, () => ({
    submit: handleSubmit,
    exportSelected: handleExportSelected,
    exportAll: handleExportAll,
  }));

  const buildExportRow = (item: IPayroll) => ({
    employerRef: item.subcontractorRef?.agencyRef || '_',
    employeeName:
      item.subcontractorRef?.fullName ||
      `${item.subcontractorRef?.forename || ''}${item.subcontractorRef?.surname || ''}` ||
      '_',
    taxPeriod: item.taxPeriod ? `${item.taxPeriod}/${item.taxYear}` : '',
    payFrequency:
      find(defaultPaymentFrequency, (f) => f?.value === item.payFrequency)
        ?.label || '',
    paymentCompany: item.companyRef?.companyName || '',
    grossPay: formatMoneyExport(item.gross, showCurrencyByCountry(payrollCountry)),
    pensionableGross: formatMoneyExport(
      item.irelandValue?.pensionGross,
      showCurrencyByCountry(payrollCountry),
    ),
    employeeContribution: formatMoneyExport(
      item.irelandValue?.eePension,
      showCurrencyByCountry(payrollCountry),
    ),
    employerContribution: formatMoneyExport(
      item.irelandValue?.erPension,
      showCurrencyByCountry(payrollCountry),
    ),
  });

  const csvHeaders = [
    'Employer Ref',
    'Employee Name',
    'Tax Period',
    'Pay Frequency',
    isBureau ? 'Employer' : 'Payment Company',
    'Gross Pay',
    'Pensionable Gross',
    'Employee Contribution',
    'Employer Contribution',
  ];

  const doExport = (data: IPayroll[]) => {
    const csvExporter = new ExportToCsv({
      filename: `Pension-pending-submissions-${moment().format('DD-MM-YYYY')}`,
      fieldSeparator: ',',
      quoteStrings: '"',
      decimalSeparator: '.',
      showLabels: true,
      useTextFile: false,
      useBom: true,
      headers: csvHeaders,
    });
    try {
      csvExporter.generateCsv(data.map(buildExportRow));
    } catch (e) {
      console.error('Export failed:', e);
      console.error(e);
    }
  };

  const handleExportSelected = () => {
    if (!selectedRows.length) {
      showMessage({ type: 'warning', content: messages['common.noSelected'] });
      return;
    }
    doExport(selectedRows);
  };

  const handleExportAll = async () => {
    if (!totalDocs) {
      showMessage({ type: 'warning', content: messages['common.noDataToExport'] });
      return;
    }
    const reqParams: Record<string, any> = { ...queryParams, page: 0, size: totalDocs };
    if (isBureauEmployer && user?.companyUserIds?.length) {
      reqParams.companyIds = user.companyUserIds;
    }
    const result = await (dispatch(aepnActions.getPayrollPending(reqParams) as any) as any).unwrap();
    doExport(result?.data || []);
  };

  const handleSubmit = async () => {
    if (!selectedRows.length) {
      showMessage({ type: 'warning', content: messages['common.noSelected'] });
      return;
    }
    const payrollIds = selectedRows.map((r) => String(r.id));
    try {
      await (dispatch(aepnActions.submitContributions({ payrollIds }) as any) as any).unwrap();
      showMessage({ type: 'info', content: 'Submitting pension contributions. Please wait...' });
    } catch (err: any) {
      console.error(err);
    }
  };

  const handleSearch = useCallback(
    debounce(
      (e: React.ChangeEvent<HTMLInputElement>) =>
        fetchData({ search: e.target.value, page: 0 }),
      500,
    ),
    [fetchData],
  );

  const handleFilter = (values: ContributionsFilterValues) => {
    fetchData({ ...values, page: 0 });
  };

  const onChangePagination = (newPage: number, newSize: number) => {
    setPageSize(newSize);
    fetchData({ page: newPage - 1, size: newSize });
  };

  const isFiltering =
    !!queryParams?.search ||
    !!queryParams?.taxPeriodFrom ||
    !!queryParams?.taxPeriodTo ||
    !!queryParams?.payFrequency;

  return (
    <>
      {actionLoading && <AppLoader />}
      <AppsHeader>
        <div className="flex justify-between flex-wrap w-full gap-4">
          <div className="flex flex-wrap gap-4">
            <AppInputSearch onChange={handleSearch} />
            <AppMenuFilter
              options={[]}
              onChange={() => {}}
              onClick={() => setIsOpenFilterModal(true)}
            />
          </div>
          <div className="flex flex-wrap gap-4 items-end">
            <TaxYearSelect
              initValue={currentTaxYear}
              onFilter={(val) => fetchData({ taxYear: val.taxYear, page: 0 })}
            />
          </div>
        </div>
      </AppsHeader>
      <AppsContent>
        <AppTableContainer
          rowKey="id"
          data={list ?? []}
          loading={loading}
          page={page}
          total={totalDocs}
          pageSize={pageSize}
          scroll={{ x: 2000, y: 700 }}
          columns={getContributionsPendingColumns({
            messages,
            payrollCountry: (payrollCountry || '') as PayrollCountryType,
            isBureau,
          })}
          onChangePagination={onChangePagination}
          rowSelection={{
            type: 'checkbox',
            onChange: (_: React.Key[], rows: any[]) => setSelectedRows(rows),
          }}
          noDataType={isFiltering ? 'noDataFound' : 'noDataToShow'}
        />
      </AppsContent>
      <ContributionsFilterModal
        isOpen={isOpenFilterModal}
        toggle={() => setIsOpenFilterModal(false)}
        onFilter={handleFilter}
      />
    </>
  );
};

export default ContributionsPendingTab;
